import React from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import type { UniqueIdentifier } from '@lumapps/lumx-dnd/types';
import { SortableListItem, SortableListItemProps } from '@lumapps/lumx-lists/components/SortableList/SortableListItem';

import { SORTABLE_CLASSNAME } from '../../constants';

import './index.scss';

const CLASSNAME = 'wrex-dnd-preview';

export interface DragAndDropPreviewProps extends React.PropsWithChildren {
    /** index of the element in the list */
    index: number;
    /** unique id of the element in drag and drop context */
    id: UniqueIdentifier;
    /** the slate rendered element to replicate */
    domElementToReplicate?: HTMLElement | null;
    /** classname to apply to the preview wrapper */
    wrapperClassNames?: string[];
    /** the color of the RTE's background */
    backgroundColor?: string /* hex only */;
}

export const DragAndDropPreview = ({
    domElementToReplicate,
    id,
    index,
    wrapperClassNames,
    backgroundColor,
}: DragAndDropPreviewProps) => {
    const { block } = useClassnames(CLASSNAME);

    const newElement = domElementToReplicate?.cloneNode(true) as HTMLElement | null;

    const entityRenderer: SortableListItemProps<object>['entityRenderer'] = React.useCallback(
        () => (
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: newElement?.innerHTML || '' }}
                className={classnames(block(), wrapperClassNames)}
            />
        ),
        [block, newElement?.innerHTML, wrapperClassNames],
    );

    return (
        <div
            style={
                {
                    width: domElementToReplicate?.clientWidth,
                    height: domElementToReplicate?.clientHeight,
                    '--background-color': backgroundColor,
                } as React.CSSProperties
            }
        >
            <SortableListItem
                className={SORTABLE_CLASSNAME}
                entity={{}}
                index={index}
                id={id}
                isDragged
                mode="vertical"
                gap="huge"
                entityRenderer={entityRenderer}
                withDragHandle
                withDirectMovement={false}
            />
        </div>
    );
};
